'use client';

import * as DS from '@aurorasolar/ds';
import { th, x } from '@xstyled/styled-components';
import { styled } from 'styled-components';

import { ButtonLink } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';
import { AffiliateId } from '@/models';

import { COMMUNITY_SOLAR_DESTINATION, RENTER_COPY } from '../data/content';
import { Ownership } from '../engine';

type Props = {
  ownership: Ownership | null;
  stateCode: string;
};

// Yellow CTA with black text. ButtonLink renders as an anchor, so also drop the
// global `a:hover` underline. && outspecifies the DS variant + global styles.
const CtaButtonLink = styled(ButtonLink)`
  && {
    background-color: ${th.color('yellow-200')};
    color: ${th.color('black')};
  }
  &&:hover:not([disabled]) {
    background-color: ${th.color('yellow-300')};
    text-decoration: none;
  }
`;

export const EstimateCta = ({ ownership, stateCode }: Props) => {
  if (ownership === 'RENT') {
    return (
      // px matches the readout strips' cell inset above so the copy lines up; mt doubles the
      // results-flex gap (5) to mark the second seam, between the comparison and the hand-off
      <DS.Flex gap={3} mt={5} px={6} textAlign="left" w="100%">
        <DS.Text as="p" color="uiBodyCopy" text={{ _: 'body16', md: 'body20' }}>
          {RENTER_COPY}
        </DS.Text>
        {/* TODO(bill-projection): renter routing unresolved (brief Section 7) — the
            destination button stays hidden until product names one */}
        {COMMUNITY_SOLAR_DESTINATION ? (
          <DS.Flex alignItems="flex-start">
            <CtaButtonLink
              affiliateId={AffiliateId.MINITOOLS}
              event={{ data: { state: stateCode }, name: TRACKING.BILL_PROJECTION_ESTIMATE_CTA_CLICKED }}
              path={COMMUNITY_SOLAR_DESTINATION}
              size="md"
              variant="cta"
            >
              Check community solar options
            </CtaButtonLink>
          </DS.Flex>
        ) : null}
      </DS.Flex>
    );
  }

  return (
    // px matches the readout strips' cell inset above so the copy lines up; mt doubles the
    // results-flex gap (5) to mark the second seam, between the comparison and the hand-off
    <DS.Flex gap={4} mt={5} px={6} textAlign="left" w="100%">
      <x.h2 text="h2">
        An installer can give you exact numbers for your roof, electricity use, and local incentives
      </x.h2>
      <DS.Text as="p" color="uiBodyCopy" text="body16">
        <span>Want to see </span>
        <strong>your</strong>
        <span>
          {' '}
          potential savings instead of state averages? Get personalized estimates from local installers in minutes
          without sharing any contact information.
        </span>
      </DS.Text>
      <DS.Flex alignItems="flex-start">
        <CtaButtonLink
          affiliateId={AffiliateId.MINITOOLS}
          event={{ data: { state: stateCode }, name: TRACKING.BILL_PROJECTION_ESTIMATE_CTA_CLICKED }}
          icon={DS.IconArrowRight}
          iconPosition="right"
          path="/quote-builder"
          size="md"
          variant="cta"
        >
          Get estimates for my home
        </CtaButtonLink>
      </DS.Flex>
    </DS.Flex>
  );
};
